"use client";

import { useAtom, useSetAtom } from "jotai";
import { useTranslations } from "next-intl";
import React, { type ReactElement } from "react";
import { toast } from "sonner";
import { ObservatoryTile } from "@/components";
import { useObservatory } from "src/hooks/sites";
import { selectedObservatoryAtom, selectedSiteAtom } from "@/store/sites";
import type { Observatory } from "@/types";
import { cva } from "class-variance-authority";
import { cn } from "@oreme/ui";

interface SitesObservatoryListItemProps {
  observatory: Observatory;
}

const sitesObservatoryListItemVariants = cva(
  "block p-0 m-0 rounded-lg overflow-hidden transition hover:cursor-pointer hover:ring-1",
  {
    variants: {
      active: {
        true: "ring-2 hover:ring-2",
        false: "",
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

/**
 * ExploreObservatoryListItem
 * @component
 * @param observatory
 * @returns {React.ReactElement}
 */
function SitesObservatoryListItem({
  observatory,
}: Readonly<SitesObservatoryListItemProps>): ReactElement {
  const t = useTranslations("sites.ObservatoryListItem");
  const { locate } = useObservatory(observatory);
  const [selectedObservatory, setSelectedObservatory] = useAtom(
    selectedObservatoryAtom,
  );
  const setSelectedSite = useSetAtom(selectedSiteAtom);
  const isActive = selectedObservatory === observatory;

  const handleClick = () => {
    if (selectedObservatory !== observatory) {
      setSelectedObservatory(observatory);
      setSelectedSite(null);

      const location = locate();
      if (!location) {
        toast(t("error.title"), {
          description: t("error.description"),
        });
      }
    } else {
      setSelectedObservatory(null);
      setSelectedSite(null);
    }
  };

  return (
    <button
      className={cn(
        sitesObservatoryListItemVariants({
          active: isActive,
        }),
      )}
      onClick={handleClick}
    >
      <ObservatoryTile
        observatory={observatory}
        isActive={selectedObservatory?.id === observatory.id}
      />
    </button>
  );
}

export { SitesObservatoryListItem, type SitesObservatoryListItemProps };
